import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../services/api';
import Toast from '../components/Toast';
import FilterableTable from '../components/FilterableTable';
import Combobox from '../components/Combobox';
import { filtreazaCantitate, normalizeazaNumar, formatCantitate } from '../utils/format';

const STATUS_LABEL = {
  noua: 'Noua', in_productie: 'In productie', finalizata: 'Finalizata',
  livrata: 'Livrata', anulata: 'Anulata'
};

// Cand comanda are loturi de produs finit, statusul afisat e defalcarea reala
// pe stari (ex: "2 vandute, 1 casata"), nu o singura eticheta - o comanda poate
// avea bucati iesite in moduri diferite. Pentru comenzi fara niciun lot (doar
// materiale, sau nimic pornit inca), ramane eticheta simpla de status.
function statusAfisat(c) {
  const parti = [];
  if (c.lotInLucru > 0) parti.push(`${c.lotInLucru} in lucru`);
  if (c.lotPeStoc > 0) parti.push(`${c.lotPeStoc} pe stoc`);
  if (c.lotVandute > 0) parti.push(`${c.lotVandute} vandut${c.lotVandute > 1 ? 'e' : 'a'}`);
  if (c.lotCasate > 0) parti.push(`${c.lotCasate} casat${c.lotCasate > 1 ? 'e' : 'a'}`);
  if (c.lotRebut > 0) parti.push(`${c.lotRebut} rebut${c.lotRebut > 1 ? '' : ''}`);
  if (parti.length === 0) return STATUS_LABEL[c.status] || c.status;
  return parti.join(', ');
}

export default function Comenzi() {
  const location = useLocation();
  const [comenzi, setComenzi] = useState([]);
  const [magazii, setMagazii] = useState([]);
  const [produse, setProduse] = useState([]);
  const [materiale, setMateriale] = useState([]);
  const [showNouaModal, setShowNouaModal] = useState(false);
  const [detaliiId, setDetaliiId] = useState(null);
  const [detalii, setDetalii] = useState(null);
  const [form, setForm] = useState({ client: '', termenLivrare: '', observatii: '', linii: [] });
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tabComenzi, setTabComenzi] = useState('activ');

  function incarcaComenzi() {
    api.get('/api/comenzi').then(res => setComenzi(res.data));
  }

  useEffect(() => {
    setLoading(true);
    Promise.all([api.get('/api/comenzi'), api.get('/api/magazii'), api.get('/api/produse'), api.get('/api/materiale')])
      .then(([c, g, p, m]) => { setComenzi(c.data); setMagazii(g.data); setProduse(p.data); setMateriale(m.data); })
      .finally(() => setLoading(false));
  }, []);

  // Daca vii de la butonul "Da in productie" din Nomenclator produse, comanda
  // tocmai creata se deschide automat, direct.
  useEffect(() => {
    if (location.state?.deschideComandaId) {
      deschideDetalii(location.state.deschideComandaId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state]);

  const LINIE_GOALA = { itemTip: 'PRODUS_FINIT', itemId: '', cantitate: '', magazieId: '' };

  function deschideNoua() {
    setForm({ client: '', termenLivrare: '', observatii: '', linii: [{ ...LINIE_GOALA }] });
    setShowNouaModal(true);
  }

  function adaugaLinie() {
    setForm({ ...form, linii: [...form.linii, { ...LINIE_GOALA }] });
  }

  function actualizeazaLinie(i, camp, valoare) {
    const linii = [...form.linii];
    linii[i] = { ...linii[i], [camp]: valoare };
    // Schimbarea tipului (material/produs finit) invalideaza itemul si magazia
    // alese anterior - listele si tipul de magazie potrivit difera intre ele.
    if (camp === 'itemTip') { linii[i].itemId = ''; linii[i].magazieId = ''; }
    setForm({ ...form, linii });
  }

  function stergeLinie(i) {
    setForm({ ...form, linii: form.linii.filter((_, idx) => idx !== i) });
  }

  async function salveazaComanda(e) {
    e.preventDefault();
    try {
      const payload = { ...form, linii: form.linii.map(l => ({ ...l, cantitate: normalizeazaNumar(l.cantitate) })) };
      await api.post('/api/comenzi', payload);
      setToast({ message: 'Comanda creata', type: 'ok' });
      setShowNouaModal(false);
      incarcaComenzi();
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Eroare la salvare', type: 'error' });
    }
  }

  async function deschideDetalii(id) {
    setDetaliiId(id);
    setExtinsLinieId(null);
    const res = await api.get(`/api/comenzi/${id}`);
    setDetalii(res.data);
    // Deschide automat acordeonul primei linii de produs finit, ca sa nu mai fie
    // nevoie de un click suplimentar - de obicei asta e ce vrei sa vezi imediat.
    const primaLiniePF = (res.data.linii || []).find(l => l.itemTip === 'PRODUS_FINIT');
    if (primaLiniePF) toggleAcordeon(primaLiniePF);
  }

  // --- Acordeon cu materialele din reteta unei linii de produs finit -----------
  const [extinsLinieId, setExtinsLinieId] = useState(null);
  const [loturiLinie, setLoturiLinie] = useState([]);
  const [loadingLoturi, setLoadingLoturi] = useState(false);
  const [extinsLotId, setExtinsLotId] = useState(null);
  const [materialeLot, setMaterialeLot] = useState([]);
  const [loadingMateriale, setLoadingMateriale] = useState(false);

  async function incarcaLoturiLinie(linieId) {
    setLoadingLoturi(true);
    try {
      const res = await api.get(`/api/loturi/pentru-linie?linieComandaId=${linieId}`);
      setLoturiLinie(res.data || []);
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Eroare la incarcarea loturilor', type: 'error' });
      setLoturiLinie([]);
    } finally {
      setLoadingLoturi(false);
    }
  }

  function toggleAcordeon(linie) {
    if (extinsLinieId === linie.id) {
      setExtinsLinieId(null);
      setExtinsLotId(null);
      return;
    }
    setExtinsLinieId(linie.id);
    setExtinsLotId(null);
    incarcaLoturiLinie(linie.id);
  }

  async function incarcaVerificareMateriale(lotId) {
    setLoadingMateriale(true);
    try {
      const res = await api.get(`/api/loturi/${lotId}/verificare-materiale`);
      setMaterialeLot(res.data.materiale || []);
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Eroare la verificarea materialelor', type: 'error' });
      setMaterialeLot([]);
    } finally {
      setLoadingMateriale(false);
    }
  }

  function toggleLot(lot) {
    if (extinsLotId === lot.id) {
      setExtinsLotId(null);
      return;
    }
    setExtinsLotId(lot.id);
    incarcaVerificareMateriale(lot.id);
  }

  async function porneesteLot(linieId) {
    try {
      await api.post('/api/loturi', { linieComandaId: linieId });
      setToast({ message: 'Lot nou pornit', type: 'ok' });
      incarcaLoturiLinie(linieId);
      incarcaComenzi();
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Eroare la pornirea lotului', type: 'error' });
    }
  }

  async function stergeLot(linieId, lotId) {
    try {
      await api.delete(`/api/loturi/${lotId}`);
      setToast({ message: 'Lot sters', type: 'ok' });
      if (extinsLotId === lotId) setExtinsLotId(null);
      incarcaLoturiLinie(linieId);
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Eroare la stergerea lotului', type: 'error' });
    }
  }

  // Finalizarea lotului (Reusit/Rebut) s-a mutat in Produse finite > tabul
  // "In productie", langa produs - aici raman doar pornirea si stergerea lotului.

  // --- "Da in productie" pe un material anume, pentru lotul deschis - o Iesire
  // normala, cu lotul deja stabilit, care cere aceleasi date ca orice Iesire:
  // cantitate, document, data (magazia se ia automat, de unde exista stoc). ----
  const [dauProductieModal, setDauProductieModal] = useState(null); // { lotId, materialId, denumire, um, disponibil }
  const [dpForm, setDpForm] = useState({ cantitate: '', document: '', dataDocument: '' });
  const [submitting, setSubmitting] = useState(false);

  function deschideDauProductie(lotId, mat) {
    setDpForm({ cantitate: String(mat.necesar), document: '', dataDocument: new Date().toISOString().slice(0, 10) });
    setDauProductieModal({ lotId, materialId: mat.materialId, denumire: mat.denumire, um: mat.um, disponibil: mat.disponibil });
  }

  async function confirmaDauProductie(e) {
    e.preventDefault();
    if (submitting) return;
    const cantitateNormalizata = normalizeazaNumar(dpForm.cantitate);
    if (!cantitateNormalizata || Number(cantitateNormalizata) <= 0) {
      setToast({ message: 'Introdu o cantitate valida', type: 'error' });
      return;
    }
    if (Number(cantitateNormalizata) > Number(dauProductieModal.disponibil)) {
      setToast({ message: `Cantitatea depaseste stocul disponibil (${formatCantitate(dauProductieModal.disponibil)} ${dauProductieModal.um})`, type: 'error' });
      return;
    }
    if (!dpForm.dataDocument) {
      setToast({ message: 'Data este obligatorie', type: 'error' });
      return;
    }
    setSubmitting(true);
    try {
      // Fara alegere de magazie - se ia automat de unde exista stoc (posibil din
      // mai multe magazii de materii prime, daca una singura nu ajunge).
      await api.post('/api/miscari/consum-productie-auto', {
        materialId: dauProductieModal.materialId, cantitate: cantitateNormalizata,
        lotProductieId: dauProductieModal.lotId,
        document: dpForm.document || null, dataDocument: dpForm.dataDocument
      });
      setToast({ message: 'Material dat in productie', type: 'ok' });
      setDauProductieModal(null);
      incarcaVerificareMateriale(dauProductieModal.lotId);
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Eroare la inregistrare', type: 'error' });
    } finally {
      setSubmitting(false);
    }
  }

  async function livreazaMaterial(linieId) {
    try {
      await api.post(`/api/comenzi/linii/${linieId}/livreaza-material`);
      setToast({ message: 'Material livrat din stoc', type: 'ok' });
      const res = await api.get(`/api/comenzi/${detaliiId}`);
      setDetalii(res.data);
      incarcaComenzi();
    } catch (err) {
      setToast({ message: err.response?.data?.error || 'Eroare la livrarea materialului', type: 'error' });
    }
  }

  return (
    <div>
      <h1>Comenzi</h1>
      <div className="toolbar">
        <button className="btn-add" onClick={deschideNoua}>+ Comanda noua</button>
      </div>

      <div className="tabs">
        <button type="button" className={`tab-btn${tabComenzi === 'activ' ? ' activ' : ''}`} onClick={() => setTabComenzi('activ')}>
          Comenzi in productie
        </button>
        <button type="button" className={`tab-btn${tabComenzi === 'inchise' ? ' activ' : ''}`} onClick={() => setTabComenzi('inchise')}>
          Comenzi inchise
        </button>
      </div>

      {loading ? <p>Se incarca...</p> : (
        <FilterableTable
          rowKey="id"
          columns={[
            { key: 'client', label: 'Client' },
            { key: 'produsPrincipal', label: 'Produs', render: c => c.produsPrincipal || '-' },
            { key: 'pretVanzareEstimat', label: 'Pret vanzare estimat', render: c => c.pretVanzareEstimat != null ? `${formatCantitate(c.pretVanzareEstimat)} lei` : '-' },
            { key: 'termenLivrare', label: 'Termen livrare', render: c => c.termenLivrare || '-' },
            { key: 'status', label: 'Status', render: c => <span className="badge ok">{statusAfisat(c)}</span> },
            { key: 'dataCreare', label: 'Creata', render: c => c.dataCreare ? new Date(c.dataCreare).toLocaleDateString('ro-RO') : '-' },
            { key: 'actiuni', label: '', filterable: false, render: c => <button className="btn-edit" onClick={() => deschideDetalii(c.id)}>Detalii</button> }
          ]}
          rows={comenzi.filter(c => tabComenzi === 'inchise' ? c.inchisa : !c.inchisa)}
        />
      )}

      {showNouaModal && (
        <div className="modal-overlay" onClick={() => setShowNouaModal(false)}>
          <div className="modal" style={{ width: 480 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Comanda noua</h2>
              <button type="button" className="modal-close" onClick={() => setShowNouaModal(false)}>&times;</button>
            </div>
            <form onSubmit={salveazaComanda}>
              <div className="modal-body">
                <div className="field-row">
                  <div className="field">
                    <label>Client *</label>
                    <input value={form.client} onChange={e => setForm({ ...form, client: e.target.value })} required autoFocus />
                  </div>
                  <div className="field">
                    <label>Termen livrare</label>
                    <input type="date" value={form.termenLivrare} onChange={e => setForm({ ...form, termenLivrare: e.target.value })} />
                  </div>
                </div>

                <label style={{ fontSize: 13, color: '#555', fontWeight: 500 }}>Produse comandate</label>
                {form.linii.map((l, i) => {
                  const itemuri = l.itemTip === 'MATERIAL' ? materiale : produse;
                  const tipMagazie = l.itemTip === 'MATERIAL' ? 'MATERII_PRIME' : 'PRODUSE_FINITE';
                  return (
                    <div key={i} style={{ display: 'flex', flexWrap: 'wrap', gap: 6, margin: '8px 0', alignItems: 'center' }}>
                      <select value={l.itemTip} onChange={e => actualizeazaLinie(i, 'itemTip', e.target.value)} style={{ flex: 1 }}>
                        <option value="PRODUS_FINIT">Produs finit</option>
                        <option value="MATERIAL">Material</option>
                      </select>
                      <select value={l.itemId} onChange={e => actualizeazaLinie(i, 'itemId', e.target.value)} required style={{ flex: 2 }}>
                        <option value="">{l.itemTip === 'MATERIAL' ? 'Material...' : 'Produs...'}</option>
                        {itemuri.map(it => <option key={it.id} value={it.id}>{it.denumire}</option>)}
                      </select>
                      <input type="text" inputMode="decimal" placeholder="Cant." style={{ width: 70 }}
                        value={l.cantitate} onChange={e => actualizeazaLinie(i, 'cantitate', filtreazaCantitate(e.target.value))} required />
                      <select value={l.magazieId} onChange={e => actualizeazaLinie(i, 'magazieId', e.target.value)} style={{ flex: 2 }}
                        title={l.itemTip === 'PRODUS_FINIT' ? 'Magazia de produse finite unde intra produsul' : 'Magazia de unde se livreaza materialul'}>
                        <option value="">{l.itemTip === 'PRODUS_FINIT' ? 'Produse finite in...' : 'Magazie...'}</option>
                        {magazii.filter(g => g.tip === tipMagazie).map(g => <option key={g.id} value={g.id}>{g.cod} - {g.nume}</option>)}
                      </select>
                      <button type="button" className="btn danger" onClick={() => stergeLinie(i)}>x</button>
                    </div>
                  );
                })}
                <button type="button" className="btn secondary" onClick={adaugaLinie} style={{ marginBottom: 8 }}>+ Adauga produs</button>

                <div className="field">
                  <label>Observatii</label>
                  <textarea rows="2" value={form.observatii} onChange={e => setForm({ ...form, observatii: e.target.value })} />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn secondary" onClick={() => setShowNouaModal(false)}>Anuleaza</button>
                <button type="submit" className="btn">Salveaza comanda</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {detaliiId && detalii && (
        <div className="modal-overlay" onClick={() => setDetaliiId(null)}>
          <div className="modal" style={{ width: 720 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Comanda - {detalii.client}</h2>
              <button type="button" className="modal-close" onClick={() => setDetaliiId(null)}>&times;</button>
            </div>
            <div className="modal-body">
              <p style={{ marginTop: 0 }}>Status: <span className="badge ok">{statusAfisat(detalii)}</span></p>
              <table>
                <thead><tr><th></th><th>Produs</th><th>Cantitate</th><th>Pret vanzare estimat</th><th>Magazie</th><th>Status linie</th><th></th></tr></thead>
                <tbody>
                  {(detalii.linii || []).map(l => (
                    <React.Fragment key={l.id}>
                      <tr onClick={() => l.itemTip === 'PRODUS_FINIT' && toggleAcordeon(l)}
                        style={{ cursor: l.itemTip === 'PRODUS_FINIT' ? 'pointer' : 'default' }}>
                        <td>{l.itemTip === 'PRODUS_FINIT' && (extinsLinieId === l.id ? '▾' : '▸')}</td>
                        <td>{l.produsDenumire}</td>
                        <td>{formatCantitate(l.cantitate)} {l.um}</td>
                        <td>{l.itemTip === 'PRODUS_FINIT' && l.pretVanzareEstimat != null ? `${formatCantitate(l.pretVanzareEstimat)} lei` : '-'}</td>
                        <td>{l.magazieNume || '-'}</td>
                        <td>{l.status}</td>
                        <td>
                          {l.status !== 'finalizata' && l.itemTip === 'MATERIAL' && (
                            <button className="btn" onClick={(e) => { e.stopPropagation(); livreazaMaterial(l.id); }}>Livreaza din stoc</button>
                          )}
                        </td>
                      </tr>
                      {l.itemTip === 'PRODUS_FINIT' && extinsLinieId === l.id && (
                        <tr>
                          <td colSpan="7" style={{ background: '#f8f9fb', padding: '10px 16px' }}>
                            {loadingLoturi ? <p style={{ margin: 0 }}>Se incarca...</p> : (
                              <>
                                <table>
                                  <thead>
                                    <tr><th></th><th>Lot</th><th>Data start</th><th>Cost acumulat</th><th>Status</th><th></th></tr>
                                  </thead>
                                  <tbody>
                                    {loturiLinie.map((lot, i) => (
                                      <React.Fragment key={lot.id}>
                                        <tr onClick={() => lot.status === 'in_lucru' && toggleLot(lot)}
                                          style={{ cursor: lot.status === 'in_lucru' ? 'pointer' : 'default' }}>
                                          <td>{lot.status === 'in_lucru' && (extinsLotId === lot.id ? '▾' : '▸')}</td>
                                          <td>Bucata {i + 1}</td>
                                          <td>{lot.dataStart ? new Date(lot.dataStart).toLocaleDateString('ro-RO') : '-'}</td>
                                          <td>{lot.costAcumulat != null ? `${formatCantitate(lot.costAcumulat)} lei` : '-'}</td>
                                          <td>
                                            {lot.status === 'in_lucru' && <span className="badge" style={{ background: '#e2e8f0', color: '#334155' }}>In lucru</span>}
                                            {lot.status === 'finalizat_reusit' && !lot.rebut && <span className="badge ok">Finalizat</span>}
                                            {lot.rebut && <span className="badge alerta">Rebut</span>}
                                          </td>
                                          <td>
                                            {lot.status === 'in_lucru' && (
                                              <button type="button" className="btn secondary" onClick={(e) => { e.stopPropagation(); stergeLot(l.id, lot.id); }}>Sterge</button>
                                            )}
                                          </td>
                                        </tr>
                                        {extinsLotId === lot.id && (
                                          <tr>
                                            <td colSpan="6" style={{ background: '#eef1f5', padding: '8px 14px' }}>
                                              {loadingMateriale ? <p style={{ margin: 0 }}>Se incarca...</p> : materialeLot.length === 0 ? (
                                                <p style={{ color: '#888', margin: 0 }}>Produsul nu are reteta (BOM) configurata.</p>
                                              ) : (
                                                <table>
                                                  <thead>
                                                    <tr><th>Nr crt</th><th>Material</th><th>Necesar (ramas de dat)</th><th>Stoc</th><th>De aprovizionat</th><th></th></tr>
                                                    {/* "Necesar" e cantitatea ramasa de dat pt acest lot (reteta - deja dat),
                                                        nu cantitatea totala din reteta - scade automat dupa fiecare consum. */}
                                                  </thead>
                                                  <tbody>
                                                    {materialeLot.map((m, j) => (
                                                      <tr key={m.materialId} className={m.diferenta != null ? 'alerta' : ''}>
                                                        <td>{j + 1}</td>
                                                        <td>{m.cod ? `${m.cod} - ` : ''}{m.denumire}</td>
                                                        <td>{formatCantitate(m.necesar)} {m.um}</td>
                                                        <td>{formatCantitate(m.disponibil)} {m.um}</td>
                                                        <td>{m.diferenta != null ? `${formatCantitate(m.diferenta)} ${m.um}` : '-'}</td>
                                                        <td>
                                                          {m.acoperit ? (
                                                            <button className="btn" disabled>Dat in productie</button>
                                                          ) : (
                                                            <button className="btn" onClick={() => deschideDauProductie(lot.id, m)}>Da in productie</button>
                                                          )}
                                                        </td>
                                                      </tr>
                                                    ))}
                                                  </tbody>
                                                </table>
                                              )}
                                            </td>
                                          </tr>
                                        )}
                                      </React.Fragment>
                                    ))}
                                    {loturiLinie.length === 0 && (
                                      <tr><td colSpan="6" style={{ color: '#888' }}>Niciun lot pornit inca pentru aceasta linie.</td></tr>
                                    )}
                                  </tbody>
                                </table>
                                <button type="button" className="btn-add" style={{ marginTop: 8 }} onClick={() => porneesteLot(l.id)}>
                                  + Porneste un lot nou (1 bucata)
                                </button>
                              </>
                            )}
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="modal-actions">
              <button type="button" className="btn secondary" onClick={() => setDetaliiId(null)}>Inchide</button>
            </div>
          </div>
        </div>
      )}

      {dauProductieModal && (
        <div className="modal-overlay" onClick={() => setDauProductieModal(null)}>
          <div className="modal" style={{ width: 400 }} onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Da in productie</h2>
              <button type="button" className="modal-close" onClick={() => setDauProductieModal(null)}>&times;</button>
            </div>
            <form onSubmit={confirmaDauProductie}>
              <div className="modal-body">
                <p style={{ marginTop: 0, color: '#888', fontSize: 13 }}>
                  {dauProductieModal.denumire} &middot; disponibil: {formatCantitate(dauProductieModal.disponibil)} {dauProductieModal.um}
                  <br />Se ia automat de unde exista stoc (materia prima nu necesita alegerea magaziei).
                </p>
                <div className="field-row">
                  <div className="field">
                    <label>Cantitate *</label>
                    <Combobox
                      options={dauProductieModal.disponibil > 0
                        ? Array.from({ length: Math.floor(dauProductieModal.disponibil) }, (_, i) => ({ value: i + 1, label: String(i + 1) }))
                        : []}
                      value={dpForm.cantitate} placeholder="Cantitate" allowFree
                      onTextChange={(txt) => setDpForm(f => ({ ...f, cantitate: filtreazaCantitate(txt) }))}
                      onSelect={(val) => setDpForm(f => ({ ...f, cantitate: val != null ? String(val) : '' }))} />
                  </div>
                </div>
                <div className="field-row">
                  <div className="field">
                    <label>Document</label>
                    <input value={dpForm.document} onChange={e => setDpForm({ ...dpForm, document: e.target.value })} />
                  </div>
                  <div className="field">
                    <label>Data *</label>
                    <input type="date" max={new Date().toISOString().slice(0, 10)}
                      value={dpForm.dataDocument} onChange={e => setDpForm({ ...dpForm, dataDocument: e.target.value })} required />
                  </div>
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn secondary" onClick={() => setDauProductieModal(null)}>Anuleaza</button>
                <button type="submit" className="btn" disabled={submitting}>{submitting ? 'Se salveaza...' : 'Confirma'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      <Toast message={toast?.message} type={toast?.type} onClose={() => setToast(null)} />
    </div>
  );
}
